import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {config} from '../../../config';
import {LocaleEnum} from '../../types/locale';
import {BasePage} from '../../types/page';
import {localeToPath} from '../../utils/localeToPath';
import {Link} from '../Link';
import {SlimIcon} from '../icon/SlimIcon';
import classNames from 'classnames';
import {DatoException} from '../../utils/exception/DatoException';
import {isHomePage} from '../../utils/isHomePage';
import {Script} from 'gatsby-script';

const MAX_LEVEL = 4;

type BreadcrumbsProps = {
  data: BasePage;
};

function getBreadcrumbs(page: Pick<BasePage, 'titleForBreadcrumb' | 'slug' | 'parentPage'>) {
  let result = [];

  const {titleForBreadcrumb, slug, parentPage} = page;

  if (parentPage) {
    result = getBreadcrumbs(parentPage);
  }

  result.push({titleForBreadcrumb, slug});

  return result;
}

export const Breadcrumbs = (props: BreadcrumbsProps) => {
  const {locale} = useIntl();
  const {titleForBreadcrumb, slug, parentPage} = props.data;

  const breadcrumbs = useMemo(() => getBreadcrumbs({titleForBreadcrumb, slug, parentPage}), [titleForBreadcrumb, slug, parentPage]);
  if (breadcrumbs.length > MAX_LEVEL) {
    throw new DatoException(
      `The breadcrumbs are broken, because the page \`/${props.data.slug}\` has too many ancestors, only ${MAX_LEVEL} levels of nesting is allowed.`,
      props.data.model.originalId,
      props.data.originalId
    );
  }
  const breadcrumbsMicrodata = useMemo(
    () =>
      breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.titleForBreadcrumb,
        item: `${config.productionHost}/${localeToPath(locale as LocaleEnum)}/${index === 0 ? '' : item.slug}`
      })),
    [breadcrumbs, locale]
  );

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbsMicrodata
  };

  return (
    <div data-testid='breadcrumbs' className='container max-w-screen-xl max-lg:px-3 py-[15px] text-xs md:text-[13px]'>
      {breadcrumbs.map((item, index) => (
        <span key={item.titleForBreadcrumb}>
          <Link
            data-testid='breadcrumb-link'
            className={classNames('leading-5', {
              'cursor-default pointer-events-none text-black': index === breadcrumbs.length - 1
            })}
            to={isHomePage(`/${item.slug}/`) ? `/${localeToPath(locale as LocaleEnum)}` : `/${localeToPath(locale as LocaleEnum)}/${item.slug}`}
          >
            {item.titleForBreadcrumb}
          </Link>
          {index < breadcrumbs.length - 1 && (
            <span className='text-grey'>
              <SlimIcon name='breadcrumb-arrow' className='w-2.5 h-2.5 mx-2 align-middle' />
            </span>
          )}
        </span>
      ))}
      <Script type='application/ld+json' dangerouslySetInnerHTML={{__html: JSON.stringify(breadcrumbSchema)}} />
    </div>
  );
};
